import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/* ---------------------------------------------------------------------------
   GW — two-tier disabled semantics for the quantity stepper.

   02-COMPONENTS §1.3: a non-CTA control that cannot act "gets
   `aria-disabled="true"`, NOT the `disabled` attribute, so it stays focusable
   and can announce *why*." 04-GUARDRAILS §5.7 gives the reason in full: a
   `disabled` control is skipped by keyboard navigation, so a keyboard shopper
   cannot even reach the thing telling her she is blocked.

   §9 names the two limit states this covers: at-minimum on the `−`, and
   at-stock-ceiling on the `+` ("sets aria-disabled and announces the ceiling
   reason"). Both are handled here, at the source, so the rendered result is
   correct at first paint AND after every re-render — a variant change, a cart
   update, or a section morph all funnel back through `updateButtonStates()`.

   `data-gw-limit` records that the flag came from this component rather than
   from the server, so a re-connect cannot mistake it for a server lock.
   --------------------------------------------------------------------------- */

/**
 * True when a control was locked by the server rather than by a live limit.
 * @param {HTMLElement | undefined} button
 * @returns {boolean}
 */
function isServerLocked(button) {
  if (!button) return false;
  if (button.dataset.gwLimit === 'true') return false;
  return button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true';
}

/**
 * True when a control is present and focusable but must not act.
 * @param {HTMLElement | undefined} button
 * @returns {boolean}
 */
function isInert(button) {
  return Boolean(button) && button?.getAttribute('aria-disabled') === 'true';
}

/* ---------------------------------------------------------------------------
   GW (integration) — the INPUT follows the same two-tier rule as the buttons.

   quantity-selector.liquid used to put a true `disabled` on the number field
   when the server said the line was locked (`can_update_quantity == false`) or
   the variant was unavailable. That is the same §1.3 defect the buttons were
   fixed for: `disabled` takes the field out of the tab order, so the keyboard
   shopper cannot reach it, cannot read the value that is locked, and cannot be
   told why. The field now renders `aria-disabled="true" readonly` instead.

   `readonly` is what actually stops typing; `aria-disabled` is what announces
   the state. Neither stops a *programmatic* write, so the handlers that mutate
   the value have to check for themselves — that is what these two do.
   --------------------------------------------------------------------------- */

/**
 * True when the quantity field itself is locked by the server.
 * @param {HTMLInputElement | undefined} input
 * @returns {boolean}
 */
function isInputLocked(input) {
  return Boolean(input) && input?.getAttribute('aria-disabled') === 'true';
}

/**
 * Puts a stepper control into (or out of) its at-a-limit state, and keeps the
 * spoken reason attached to it.
 * @param {HTMLElement | undefined} button
 * @param {boolean} atLimit
 * @param {string} reason
 */
export function setLimitState(button, atLimit, reason) {
  if (!button) return;

  // Never the true attribute. If anything upstream set it, take it back off.
  if (button.hasAttribute('disabled')) button.removeAttribute('disabled');

  if (!atLimit) {
    button.removeAttribute('aria-disabled');
    delete button.dataset.gwLimit;
    setReason(button, '');
    return;
  }

  button.setAttribute('aria-disabled', 'true');
  button.dataset.gwLimit = 'true';
  setReason(button, reason);
}

/**
 * Writes the reason into a visually-hidden description alongside the control,
 * so it is announced on focus without changing the control's accessible name.
 * @param {HTMLElement} button
 * @param {string} reason
 */
function setReason(button, reason) {
  const existing = button.nextElementSibling;
  const isReasonNode = existing instanceof HTMLElement && existing.dataset.gwQtyReason === 'true';

  if (!reason) {
    if (isReasonNode) existing.textContent = '';
    return;
  }

  if (isReasonNode) {
    if (existing.textContent !== reason) existing.textContent = reason;
    return;
  }

  if (!button.id) {
    button.id = `gw-qty-${Math.random().toString(36).slice(2, 9)}`;
  }
  const node = document.createElement('span');
  node.className = 'visually-hidden';
  node.dataset.gwQtyReason = 'true';
  node.id = `${button.id}-reason`;
  node.textContent = reason;
  button.insertAdjacentElement('afterend', node);
  button.setAttribute('aria-describedby', node.id);
}

/**
 * A custom element that allows the user to select a quantity.
 *
 * This component follows a pure event-driven architecture where quantity changes
 * are broadcast via QuantitySelectorUpdateEvent. Parent components that contain
 * quantity selectors listen for these events and handle them according to their
 * specific needs, with event filtering ensuring each parent only processes events
 * from its own quantity selectors to prevent conflicts between different cart
 * update strategies.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    // Capture server-disabled state on first load.
    // GW: the server now renders `aria-disabled="true"` rather than `disabled`
    // (02-COMPONENTS §1.3), so both spellings count as "locked by the server".
    // `data-gw-limit` marks the state THIS component set, so a re-connect does
    // not mistake our own at-a-limit flag for a server lock and latch it on.
    const { minusButton, plusButton } = this.refs;

    this.serverDisabledMinus = isServerLocked(minusButton);
    this.serverDisabledPlus = isServerLocked(plusButton);

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Whether a locked quantity FIELD should also stop the two steppers.
   *
   * On a product surface it must: the field is locked because there is no
   * quantity to choose, so a `+` that still counted up would be lying. The cart
   * overrides this to `false` — a cart line can render a locked field because
   * its variant went unavailable, and the shopper still has to be able to bring
   * that line down. There, `can_update_quantity` is the authority and it already
   * locks the buttons at the source.
   * @returns {boolean}
   */
  get respectsInputLock() {
    return true;
  }

  /**
   * The reason announced when the `−` cannot go lower. Read from the element so
   * the string stays in `locales/`, and so the cart can give a different reason
   * (there, the way out is Remove) than a product page.
   * @returns {string}
   */
  get minimumReason() {
    return this.dataset.gwMinReason || '';
  }

  /**
   * The reason announced when the `+` is at the stock ceiling (02-COMPONENTS §9:
   * "at-stock-ceiling (`+` sets aria-disabled and announces the ceiling reason)").
   * @returns {string}
   */
  get maximumReason() {
    return this.dataset.gwMaxReason || '';
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      setLimitState(minusButton, value <= min, this.minimumReason);
    }

    if (!this.serverDisabledPlus) {
      setLimitState(plusButton, effectiveMax !== null && value >= effectiveMax, this.maximumReason);
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value + step * stepMultiplier));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    // `aria-disabled` does not stop the click the way `disabled` does, so the
    // guard moves into the handler. The value clamp below would make this a
    // no-op anyway; this keeps it from dispatching a pointless update event.
    if (isInert(this.refs.plusButton)) return;
    if (this.respectsInputLock && isInputLocked(this.refs.quantityInput)) return;
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    if (isInert(this.refs.minusButton)) return;
    if (this.respectsInputLock && isInputLocked(this.refs.quantityInput)) return;
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;
    // A locked field is readable and copyable, but select-all is an editing
    // affordance — it invites a keystroke the field will refuse.
    if (isInputLocked(quantityInput)) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    // `readonly` blocks typing but not a programmatic write, and this handler
    // fires on blur regardless. Guard it the way the buttons guard themselves.
    if (isInputLocked(this.refs.quantityInput)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
